import Link from 'next/link'

/** Shared CTA style matching the homepage hero "Start Rating" button. */
export const RATE_CTA_CLASS =
  'block w-full px-6 py-3 bg-[#FEDB1A] text-[#1E4289] font-bold rounded shadow-lg hover:bg-white transition-colors text-center'

export function RateCtaLabel({ label = 'Rate It' }: { label?: string }) {
  return <span className={RATE_CTA_CLASS}>{label}</span>
}

interface StarRatingProps {
  rating: number
  size?: 'sm' | 'md' | 'lg'
  showNumber?: boolean
  totalRatings?: number
  /** Visual variant — `community` is the default blue, `user` is the
   *  orange accent used for an individual user's rating, and `period`
   *  is the sky‑blue accent used for time‑windowed averages
   *  (last week, last month, last six months). */
  variant?: 'community' | 'user' | 'period'
  /** Layout direction — `vertical` (default) stacks number, stars and
   *  count; `horizontal` lays them out in a single row. */
  orientation?: 'vertical' | 'horizontal'
  /**
   * When set, shows a "Rate" link to `/rate/[slug]` (or `rateHref`).
   * Do not pass this when the parent is already an `<a>`/`Link`.
   */
  entitySlug?: string
  /** @deprecated Use entitySlug — kept for temporary compatibility */
  entityId?: string
  /** Override the rate destination (e.g. `#rating-form` on the entity page). */
  rateHref?: string
  /** Show a non-link Rate CTA (use when the parent is already a link). */
  showRateLabel?: boolean
}

export function StarRating({
  rating,
  size = 'md',
  showNumber = false,
  totalRatings,
  variant = 'community',
  orientation = 'vertical',
  entitySlug,
  entityId,
  rateHref,
  showRateLabel = false,
}: StarRatingProps) {
  const sizeConfig = {
    sm: { square: 'w-4 h-4', number: 'text-lg', countText: 'text-xs' },
    md: { square: 'w-5 h-5', number: 'text-xl', countText: 'text-xs' },
    lg: { square: 'w-6 h-6', number: 'text-2xl', countText: 'text-sm' },
  }

  const variantConfig = {
    community: { number: 'text-[#1E4289]', fill: 'bg-[#1E4289]' },
    user: { number: 'text-[#E79D20]', fill: 'bg-[#E79D20]' },
    period: { number: 'text-[#0EA5E9]', fill: 'bg-[#0EA5E9]' },
  }

  const config = sizeConfig[size]
  const colors = variantConfig[variant]

  // Round to one decimal so partial-fill widths are stable across SSR/client
  // (avoids float noise like 0.09999999999999964 → "9.999…%" hydration mismatch).
  const tenths = Math.round(Math.max(0, Math.min(5, rating)) * 10) / 10
  const fullStars = Math.floor(tenths)
  const partialTenths = Math.round((tenths - fullStars) * 10)
  const partialStar = partialTenths / 10
  const emptyStars = 5 - fullStars - (partialStar > 0 ? 1 : 0)

  const isHorizontal = orientation === 'horizontal'
  const pathSegment = entitySlug || entityId
  const href = rateHref ?? (pathSegment ? `/rate/${pathSegment}` : undefined)
  const showCta = Boolean(href) || showRateLabel

  const stars = (
    <div className={isHorizontal ? 'flex flex-row items-center gap-2' : 'flex flex-col'}>
      {showNumber && (
        <span className={`${config.number} font-bold ${colors.number}`}>
          {tenths.toFixed(1)}
        </span>
      )}

      <div className="flex gap-0.5">
        {Array.from({ length: fullStars }).map((_, i) => (
          <div
            key={`full-${i}`}
            className={`${config.square} ${colors.fill} rounded-sm flex items-center justify-center`}
          >
            <svg
              className="w-3/4 h-3/4 text-white fill-white"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
              />
            </svg>
          </div>
        ))}

        {partialStar > 0 && (
          <div
            className={`${config.square} rounded-sm flex items-center justify-center overflow-hidden relative`}
          >
            <div
              className={`absolute inset-0 ${colors.fill}`}
              style={{ width: `${partialTenths * 10}%` }}
            />
            <div
              className="absolute inset-0 bg-slate-300"
              style={{ left: `${partialTenths * 10}%` }}
            />
            <svg
              className="w-3/4 h-3/4 text-white fill-white relative z-10"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
              />
            </svg>
          </div>
        )}

        {Array.from({ length: emptyStars }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className={`${config.square} bg-slate-300 rounded-sm flex items-center justify-center`}
          >
            <svg
              className="w-3/4 h-3/4 text-white fill-white"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
              />
            </svg>
          </div>
        ))}
      </div>

      {totalRatings !== undefined && (
        <span className={`${config.countText} text-slate-500 ${isHorizontal ? '' : 'mt-0.5'}`}>
          {totalRatings} rating{totalRatings !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  )

  if (!showCta) {
    return stars
  }

  const cta = <RateCtaLabel />

  if (href) {
    return (
      <div className="w-full flex flex-col gap-3">
        {stars}
        <Link href={href} className={RATE_CTA_CLASS} aria-label="Rate this">
          Rate It
        </Link>
      </div>
    )
  }

  return (
    <div className="w-full flex flex-col gap-3">
      {stars}
      {cta}
    </div>
  )
}
